import "@/App.css";
import { Component, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { ProductsProvider } from "@/contexts/ProductsContext";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import Setup from "@/pages/Setup";
import ForceChangePassword from "@/pages/ForceChangePassword";
import Dashboard from "@/pages/Dashboard";
import ProductList from "@/pages/products/ProductList";
import ProductForm from "@/pages/products/ProductForm";
import ProductDetail from "@/pages/products/ProductDetail";
import WarehouseList from "@/pages/warehouses/WarehouseList";
import WarehouseDetail from "@/pages/warehouses/WarehouseDetail";
import StockMovements from "@/pages/stock/StockMovements";
import StockCounts from "@/pages/stock/StockCounts";
import StockCountNew from "@/pages/stock/StockCountNew";
import StockCountDetail from "@/pages/stock/StockCountDetail";
import WasteRecords from "@/pages/stock/WasteRecords";
import RecipeList from "@/pages/recipes/RecipeList";
import RecipeForm from "@/pages/recipes/RecipeForm";
import RecipeDetail from "@/pages/recipes/RecipeDetail";
import ProductionOrders from "@/pages/production/ProductionOrders";
import ProductionOrderNew from "@/pages/production/ProductionOrderNew";
import ConsumptionNew from "@/pages/production/ConsumptionNew";
import ConsumptionDetail from "@/pages/production/ConsumptionDetail";
import SupplierList from "@/pages/suppliers/SupplierList";
import SupplierNew from "@/pages/suppliers/SupplierNew";
import SupplierDetail from "@/pages/suppliers/SupplierDetail";
import PurchaseRequestNew from "@/pages/purchasing/PurchaseRequestNew";
import PurchaseRequestDetail from "@/pages/purchasing/PurchaseRequestDetail";
import PurchaseOrders from "@/pages/purchasing/PurchaseOrders";
import PurchaseOrderNew from "@/pages/purchasing/PurchaseOrderNew";
import PurchaseOrderDetail from "@/pages/purchasing/PurchaseOrderDetail";
import PurchaseReceiptNew from "@/pages/purchasing/PurchaseReceiptNew";
import PurchaseReceiptEdit from "@/pages/purchasing/PurchaseReceiptEdit";
import MalKabulList from "@/pages/purchasing/MalKabulList";
import MalKabulForm from "@/pages/purchasing/MalKabulForm";
import Purchasing from "@/pages/purchasing/Purchasing";
import DosyaKutusu from "@/pages/dosya/DosyaKutusu";
import Expenses from "@/pages/purchasing/Expenses";
import TransferList from "@/pages/transfers/TransferList";
import InvoiceList from "@/pages/invoices/InvoiceList";
import InvoiceNew from "@/pages/invoices/InvoiceNew";
import InvoiceDetail from "@/pages/invoices/InvoiceDetail";
import AdminPanel from "@/pages/admin/AdminPanel";
import PosIntegration from "@/pages/admin/PosIntegration";
import PosTransfer from "@/pages/admin/PosTransfer";
import BarcodeAssign from "@/pages/BarcodeAssign";
import ExpenseItemsList from "@/pages/expenses/ExpenseItemsList";
import EmployeeList from "@/pages/hr/EmployeeList";
import EmployeeDetail from "@/pages/hr/EmployeeDetail";
import Izinler from "@/pages/hr/Izinler";
import AdvanceList from "@/pages/hr/AdvanceList";
import PayrollList from "@/pages/hr/PayrollList";
import TreasuryDashboard from "@/pages/treasury/TreasuryDashboard";
import AccountDetail from "@/pages/treasury/AccountDetail";
import PaymentCalendar from "@/pages/treasury/PaymentCalendar";
import SalesList from "@/pages/sales/SalesList";
import SatisFaturaList from "@/pages/satis/SatisFaturaList";
import SatisFaturaNew from "@/pages/satis/SatisFaturaNew";
import Timesheet from "@/pages/hr/Timesheet";
import SiteList from "@/pages/insaat/SiteList";
import SiteDetail from "@/pages/insaat/SiteDetail";
import SubcontractorList from "@/pages/insaat/SubcontractorList";
import ReportList from "@/pages/insaat/ReportList";
import ReportDetail from "@/pages/insaat/ReportDetail";
import IsTakibi from "@/pages/insaat/IsTakibi";
import PublicDailyReportForm from "@/pages/PublicDailyReportForm";
import PublicEmployeeForm from "@/pages/PublicEmployeeForm";
import PublicPuantajForm from "@/pages/PublicPuantajForm";
import PublicEkstre from "@/pages/PublicEkstre";
import WhatsAppBildirimleri from "@/pages/WhatsAppBildirimleri";
import PersonelPortal from "@/pages/PersonelPortal";
import Organizasyon from "@/pages/hr/Organizasyon";

class PageErrorBoundary extends Component {
  constructor(props) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(error) { return { error }; }
  render() {
    if (this.state.error) {
      return (
        <div style={{ padding: '40px', fontFamily: 'monospace' }}>
          <h2 style={{ color: '#7a3535' }}>Sayfa yüklenemedi</h2>
          <pre style={{ background: '#faf7f2', padding: '16px', borderRadius: '8px', fontSize: '12px', overflowX: 'auto', color: '#3F3E39' }}>
            {this.state.error?.message}
            {'\n\n'}
            {this.state.error?.stack}
          </pre>
          <button onClick={() => { this.setState({ error: null }); window.location.href = '/erp'; }}
            style={{ marginTop: '16px', padding: '8px 16px', cursor: 'pointer' }}>
            Ana Sayfaya Dön
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

function ProtectedRoute({ children }) {
  const { token, company, user } = useAuth();
  if (!token) {
    // Mevcut konumu kaydet — giriş sonrası buraya dönelim
    const current = window.location.pathname + window.location.search;
    if (current && current !== '/login') {
      sessionStorage.setItem('redirect_after_login', current);
    }
    return <Navigate to="/login" replace />;
  }
  if (!company) return <Navigate to="/select-company" replace />;
  // Geçici şifreli kullanıcıları zorunlu şifre değiştirme sayfasına yönlendir
  if (user?.is_temporary_password) return <Navigate to="/force-change-password" replace />;
  return <Layout><PageErrorBoundary>{children}</PageErrorBoundary></Layout>;
}

function AppRoutes() {
  const { token, company, user } = useAuth();
  return (
    <Routes>
      <Route path="/setup" element={<Setup />} />
      <Route path="/login" element={
        token && company ? <Navigate to="/erp" replace /> :
        token && !company ? <Navigate to="/select-company" replace /> :
        <Login />
      } />
      <Route path="/select-company" element={
        !token ? <Navigate to="/login" replace /> :
        company ? <Navigate to={sessionStorage.getItem('redirect_after_login') || '/erp'} replace /> :
        <Login initialStep="company" />
      } />
      <Route path="/force-change-password" element={
        !token ? <Navigate to="/login" replace /> :
        !company ? <Navigate to="/select-company" replace /> :
        !user?.is_temporary_password ? <Navigate to="/erp" replace /> :
        <ForceChangePassword />
      } />
      <Route path="/barkod" element={<BarcodeAssign />} />
      {/* Taseron gunluk rapor linki — girissiz, token ile cozulur */}
      <Route path="/rapor/:token" element={<PublicDailyReportForm />} />
      {/* Personel bilgi formu — girissiz, Layout DISINDA (bkz. /rapor/:token) */}
      <Route path="/personel-formu/:token" element={<PublicEmployeeForm />} />
      {/* Günlük puantaj bağlantısı — girişsiz, Layout DIŞINDA (amir telefondan açar) */}
      <Route path="/puantaj/:token" element={<PublicPuantajForm />} />
      <Route path="/ekstre/:token" element={<PublicEkstre />} />
      {/* Personel portalı — girişsiz alan, kendi e-posta + tek kullanımlık koduyla */}
      <Route path="/portal" element={<PersonelPortal />} />
      <Route path="/erp" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/erp/products" element={<ProtectedRoute><ProductList /></ProtectedRoute>} />
      <Route path="/erp/products/new" element={<ProtectedRoute><ProductForm /></ProtectedRoute>} />
      <Route path="/erp/products/:id" element={<ProtectedRoute><ProductDetail /></ProtectedRoute>} />
      <Route path="/erp/products/:id/edit" element={<ProtectedRoute><ProductForm /></ProtectedRoute>} />
      <Route path="/erp/warehouses" element={<ProtectedRoute><WarehouseList /></ProtectedRoute>} />
      <Route path="/erp/warehouses/:id" element={<ProtectedRoute><WarehouseDetail /></ProtectedRoute>} />
      <Route path="/erp/stock/movements" element={<ProtectedRoute><StockMovements /></ProtectedRoute>} />
      <Route path="/erp/stock/counts" element={<ProtectedRoute><StockCounts /></ProtectedRoute>} />
      <Route path="/erp/stock/counts/new" element={<ProtectedRoute><StockCountNew /></ProtectedRoute>} />
      <Route path="/erp/stock/counts/:id" element={<ProtectedRoute><StockCountDetail /></ProtectedRoute>} />
      <Route path="/erp/stock/waste" element={<ProtectedRoute><WasteRecords /></ProtectedRoute>} />
      <Route path="/erp/recipes" element={<ProtectedRoute><RecipeList /></ProtectedRoute>} />
      <Route path="/erp/recipes/new" element={<ProtectedRoute><RecipeForm /></ProtectedRoute>} />
      <Route path="/erp/recipes/:id" element={<ProtectedRoute><RecipeDetail /></ProtectedRoute>} />
      <Route path="/erp/recipes/:id/edit" element={<ProtectedRoute><RecipeForm /></ProtectedRoute>} />
      <Route path="/erp/production-orders" element={<ProtectedRoute><ProductionOrders /></ProtectedRoute>} />
      <Route path="/erp/production-orders/new" element={<ProtectedRoute><ProductionOrderNew /></ProtectedRoute>} />
      {/* Ayni form duzenlemede de kullaniliyor: iki kopya bir gun ayrisirdi */}
      <Route path="/erp/production-orders/:id/duzenle" element={<ProtectedRoute><ProductionOrderNew /></ProtectedRoute>} />
      <Route path="/erp/consumptions/new" element={<ProtectedRoute><ConsumptionNew /></ProtectedRoute>} />
      <Route path="/erp/consumptions/:id/edit" element={<ProtectedRoute><ConsumptionNew /></ProtectedRoute>} />
      <Route path="/erp/consumptions/:id" element={<ProtectedRoute><ConsumptionDetail /></ProtectedRoute>} />
      <Route path="/erp/suppliers" element={<ProtectedRoute><SupplierList /></ProtectedRoute>} />
      <Route path="/erp/suppliers/new" element={<ProtectedRoute><SupplierNew /></ProtectedRoute>} />
      <Route path="/erp/suppliers/:id" element={<ProtectedRoute><SupplierDetail /></ProtectedRoute>} />
      {/* Ayri talep LISTESI kaldirildi — liste "Siparis ve Mal Kabul"
          sayfasinin Talepler sekmesinde. Eski yer imleri oraya dussun. */}
      <Route path="/erp/purchase-requests" element={<Navigate to="/erp/purchasing?tab=requests" replace />} />
      <Route path="/erp/purchase-requests/new" element={<ProtectedRoute><PurchaseRequestNew /></ProtectedRoute>} />
      {/* Duzenleme rotasi detay rotasindan ONCE gelmeli: "/:id" kalibi
          "/:id/edit" yolunu yutmaz ama sira okunurlugu icin bu sekilde. */}
      <Route path="/erp/purchase-requests/:id/edit" element={<ProtectedRoute><PurchaseRequestNew /></ProtectedRoute>} />
      <Route path="/erp/purchase-requests/:id" element={<ProtectedRoute><PurchaseRequestDetail /></ProtectedRoute>} />
      <Route path="/erp/purchase-orders" element={<ProtectedRoute><PurchaseOrders /></ProtectedRoute>} />
      <Route path="/erp/purchase-orders/new" element={<ProtectedRoute><PurchaseOrderNew /></ProtectedRoute>} />
      <Route path="/erp/purchase-orders/:id/edit" element={<ProtectedRoute><PurchaseOrderNew /></ProtectedRoute>} />
      <Route path="/erp/purchase-orders/:id" element={<ProtectedRoute><PurchaseOrderDetail /></ProtectedRoute>} />
      {/* YENI mal kabul (teslimat belgesi): irsaliye / veresiye fisi / siparis fisi.
          Eski /erp/purchase-receipts/* rotalari siparise bagli ESKI akis; yeni
          kayit oraya yazilmiyor, mevcut 3 kayit icin acik birakildi. */}
      <Route path="/erp/mal-kabul" element={<ProtectedRoute><MalKabulList /></ProtectedRoute>} />
      <Route path="/erp/mal-kabul/yeni" element={<ProtectedRoute><MalKabulForm /></ProtectedRoute>} />
      <Route path="/erp/mal-kabul/:id/duzenle" element={<ProtectedRoute><MalKabulForm /></ProtectedRoute>} />
      <Route path="/erp/purchase-receipts/new" element={<ProtectedRoute><PurchaseReceiptNew /></ProtectedRoute>} />
      <Route path="/erp/purchase-receipts/:id/edit" element={<ProtectedRoute><PurchaseReceiptEdit /></ProtectedRoute>} />
      <Route path="/erp/purchase-receipts/:id" element={<ProtectedRoute><PurchaseReceiptEdit /></ProtectedRoute>} />
      <Route path="/erp/purchasing" element={<ProtectedRoute><Purchasing /></ProtectedRoute>} />
      <Route path="/erp/dosya-kutusu" element={<ProtectedRoute><DosyaKutusu /></ProtectedRoute>} />
      <Route path="/erp/expenses" element={<ProtectedRoute><Expenses /></ProtectedRoute>} />
      <Route path="/erp/transfers" element={<ProtectedRoute><TransferList /></ProtectedRoute>} />
      <Route path="/erp/invoices" element={<ProtectedRoute><InvoiceList /></ProtectedRoute>} />
      <Route path="/erp/invoices/new" element={<ProtectedRoute><InvoiceNew /></ProtectedRoute>} />
      <Route path="/erp/invoices/:id/edit" element={<ProtectedRoute><InvoiceNew /></ProtectedRoute>} />
      <Route path="/erp/invoices/:id" element={<ProtectedRoute><InvoiceDetail /></ProtectedRoute>} />
      <Route path="/erp/expense-items" element={<ProtectedRoute><ExpenseItemsList /></ProtectedRoute>} />
      <Route path="/erp/admin" element={<ProtectedRoute><AdminPanel /></ProtectedRoute>} />
      <Route path="/erp/pos-integration" element={<ProtectedRoute><PosIntegration /></ProtectedRoute>} />
      <Route path="/erp/pos-transfer" element={<ProtectedRoute><PosTransfer /></ProtectedRoute>} />
      <Route path="/erp/hr/employees" element={<ProtectedRoute><EmployeeList /></ProtectedRoute>} />
      <Route path="/erp/hr/employees/:id" element={<ProtectedRoute><EmployeeDetail /></ProtectedRoute>} />
      <Route path="/erp/hr/leaves" element={<ProtectedRoute><Izinler /></ProtectedRoute>} />
      <Route path="/erp/hr/organizasyon" element={<ProtectedRoute><Organizasyon /></ProtectedRoute>} />
      <Route path="/erp/hr/advances" element={<ProtectedRoute><AdvanceList /></ProtectedRoute>} />
      <Route path="/erp/hr/payroll" element={<ProtectedRoute><PayrollList /></ProtectedRoute>} />
      <Route path="/erp/hr/timesheet" element={<ProtectedRoute><Timesheet /></ProtectedRoute>} />
      <Route path="/erp/treasury" element={<ProtectedRoute><TreasuryDashboard /></ProtectedRoute>} />
      <Route path="/erp/treasury/accounts/:id" element={<ProtectedRoute><AccountDetail /></ProtectedRoute>} />
      <Route path="/erp/payment-calendar" element={<ProtectedRoute><PaymentCalendar /></ProtectedRoute>} />
      <Route path="/erp/sales" element={<ProtectedRoute><SalesList /></ProtectedRoute>} />
      <Route path="/erp/satis-faturalari" element={<ProtectedRoute><SatisFaturaList /></ProtectedRoute>} />
      <Route path="/erp/satis-faturalari/yeni" element={<ProtectedRoute><SatisFaturaNew /></ProtectedRoute>} />
      <Route path="/erp/satis-faturalari/:id/duzenle" element={<ProtectedRoute><SatisFaturaNew /></ProtectedRoute>} />
      <Route path="/erp/insaat/sites" element={<ProtectedRoute><SiteList /></ProtectedRoute>} />
      <Route path="/erp/insaat/sites/:id" element={<ProtectedRoute><SiteDetail /></ProtectedRoute>} />
      <Route path="/erp/insaat/subcontractors" element={<ProtectedRoute><SubcontractorList /></ProtectedRoute>} />
      <Route path="/erp/insaat/reports" element={<ProtectedRoute><ReportList /></ProtectedRoute>} />
      <Route path="/erp/insaat/reports/:id" element={<ProtectedRoute><ReportDetail /></ProtectedRoute>} />
      <Route path="/erp/insaat/isler" element={<ProtectedRoute><IsTakibi /></ProtectedRoute>} />
      <Route path="/erp/whatsapp" element={<ProtectedRoute><WhatsAppBildirimleri /></ProtectedRoute>} />
      <Route path="*" element={<Navigate to="/erp" replace />} />
    </Routes>
  );
}

function App() {
  // Sayı alanlarında fare tekerleği değeri değiştirmesin.
  // `<input type="number">` odaktayken tekerlek değeri artırıp azaltıyor;
  // uzun listelerde sayfayı kaydırırken miktar/fiyat sessizce değişiyordu.
  // Odaktan çıkarmak değeri korur ve sayfanın kaymasını da engellemez
  // (preventDefault kullanılsaydı imleç alanın üstündeyken sayfa donardı).
  useEffect(() => {
    const tekerlek = (e) => {
      const el = document.activeElement;
      if (el && el.tagName === 'INPUT' && el.type === 'number' && el === e.target) {
        el.blur();
      }
    };
    document.addEventListener('wheel', tekerlek, { passive: true });
    return () => document.removeEventListener('wheel', tekerlek);
  }, []);

  return (
    <BrowserRouter>
      <AuthProvider>
        <ProductsProvider>
        <AppRoutes />
        <Toaster
          theme="light"
          position="top-right"
          toastOptions={{
            style: {
              background: '#faf7f2',
              border: '1px solid #d0c4b0',
              color: '#1a1916',
              fontFamily: 'Manrope, sans-serif'
            },
            duration: 3000,  // Varsayılan süre: 3 saniye (başarı mesajları)
          }}
        />
        </ProductsProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
